import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNacharbeitenTickets, resolveHubSpotOwnerIdsByEmail } from '../services/hubspotBackendClient';
import { HubSpotDeal, NacharbeitenTicketListItem, ProjectStatusPhase } from '../types/hubspot';
import { useAuth } from '../contexts/AuthContext';
import { useDealsStore } from '../store/dealsStore';
import ViewToggle from '../components/ViewToggle';
import { getDealSummary, mapDealToDocuments } from '../services/hubspotMapper';
import AppHeader from '../components/AppHeader';
import ProjectScopeTabs from '../components/ProjectScopeTabs';
import DealStatusFilterBar, { DealStageFilter, InterneFreigabeFilter, NacharbeitenFilter } from '../components/DealStatusFilterBar';
import ProjectMilestoneTimeline from '../components/ProjectMilestoneTimeline';
import { useToast } from '../contexts/ToastContext';
import {
  getDefaultProjectScope,
  matchesProjectScope,
  persistProjectScope,
  PersistedProjectScope,
} from '../utils/projectScope';

type PhaseFilter = 'all' | 'projektdokumentation' | 'durchfuehrung' | 'inbetriebnahme' | 'abschlussbericht';
type SortKey = 'dealname' | 'dealstage' | 'completion' | 'requiredMissing';
type SortDirection = 'asc' | 'desc';

interface EnrichedDeal {
  deal: HubSpotDeal;
  completionRatio: number;
  summary: ReturnType<typeof getDealSummary>;
  nextStep: string;
  phaseHealth: ProjectStatusPhase[];
  projectCompletionRatio: number;
  projectProgressLoaded: boolean;
  projectRequiredMissing: number;
  currentPhaseKey: PhaseFilter;
}

function getInterneFreigabeStatus(deal: HubSpotDeal): "draft" | "in_review" | "ready" | "approved" | null {
  const direct = deal.project_status?.projektdokumentationApprovalStatus;
  if (direct === "draft" || direct === "in_review" || direct === "ready" || direct === "approved") {
    return direct;
  }

  const projektdokumentationPhase = (deal.project_status?.phases || []).find(
    (phase) => phase.phaseKey === "projektdokumentation"
  );

  if (!projektdokumentationPhase) {
    return null;
  }

  // Legacy fallback: ältere Snapshots hatten das Approval-Statusfeld noch nicht.
  // Wenn approvedByEmail fehlt, ist "completed" sehr wahrscheinlich "ready".
  if (projektdokumentationPhase.status === "completed") {
    return projektdokumentationPhase.approvedByEmail ? "approved" : "ready";
  }

  if (projektdokumentationPhase.status === "open") {
    return "draft";
  }

  // Legacy-Heuristik: ältere Snapshots ohne explizites Approval-Statusfeld.
  if (projektdokumentationPhase.status === "in_progress") {
    const completion = Number(projektdokumentationPhase.completionPercent || 0);
    const missingRequired = Number(projektdokumentationPhase.missingRequiredCount || 0);
    if (completion >= 100 && missingRequired === 0) {
      return "ready";
    }
    if (completion > 0) {
      return "in_review";
    }
  }

  return null;
}

function formatPhaseLabel(phase: string) {
  return phase.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function getCurrentPhaseKey(phases: ProjectStatusPhase[]): PhaseFilter {
  const activePhase = phases.find((phase) => phase.status === 'in_progress')
    || phases.find((phase) => phase.status === 'open');

  if (!activePhase) {
    return 'abschlussbericht';
  }

  return activePhase.phaseKey;
}

function getSortValue(entry: EnrichedDeal, sortKey: SortKey): string | number {
  switch (sortKey) {
    case 'dealstage':
      return entry.deal.dealstage_label || entry.deal.dealstage || '';
    case 'completion':
      return entry.projectCompletionRatio;
    case 'requiredMissing':
      return entry.projectRequiredMissing;
    case 'dealname':
    default:
      return entry.deal.dealname || '';
  }
}

const DEAL_STAGE_FILTER_LABELS: Record<Exclude<DealStageFilter, 'all'>, string> = {
  'kunde-wartet': 'Kunde wartet',
  'projekt-angenommen': 'Projekt Angenommen',
  'projekt-abgeschlossen': 'Projekt Abgeschlossen',
  'durchfuehrung-abgeschlossen': 'Durchführung abgeschlossen',
  waiting: 'waiting',
  dubletten: 'Dubletten',
  'anfrage-zum-kennenlernen-verschicken': 'Anfrage zum Kennenlernen verschicken',
};

export default function HubSpotDealsPage() {
  const hubspotPortalId = (import.meta.env.VITE_HUBSPOT_PORTAL_ID || '144820044').toString().trim();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    deals,
    searchResults,
    isLoading: loading,
    isLoadingMore,
    hasMore,
    error,
    loadMoreError,
    isSearching,
    searchError,
    fetchDeals,
    searchDealsIntoCache,
    clearLoadMoreError,
  } = useDealsStore();
  const { showToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [hubspotOwnerIds, setHubspotOwnerIds] = useState<string[]>([]);
  const [dealStageFilter, setDealStageFilter] = useState<DealStageFilter>('all');
  const [nacharbeitenFilter, setNacharbeitenFilter] = useState<NacharbeitenFilter>('all');
  const [interneFreigabeFilter, setInterneFreigabeFilter] = useState<InterneFreigabeFilter>('all');
  const [scope, setScope] = useState<PersistedProjectScope>(() => getDefaultProjectScope());
  const [sortKey, setSortKey] = useState<SortKey>('requiredMissing');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedDealId, setExpandedDealId] = useState<string | null>(null);
  const [nacharbeitenByDealId, setNacharbeitenByDealId] = useState<Record<string, NacharbeitenTicketListItem[]>>({});
  const [nacharbeitenLoadingByDealId, setNacharbeitenLoadingByDealId] = useState<Record<string, boolean>>({});
  const [nacharbeitenErrorByDealId, setNacharbeitenErrorByDealId] = useState<Record<string, string>>({});
  const skipNextFilterFetchRef = useRef(false);

  useEffect(() => {
    const email = user?.email?.trim();
    if (!email) {
      setHubspotOwnerIds([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      const ownerIds = await resolveHubSpotOwnerIdsByEmail(email);
      if (cancelled) return;
      setHubspotOwnerIds(ownerIds);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  useEffect(() => {
    if (!loadMoreError) return;
    showToast('warning', 'Weitere Projekte konnten nicht nachgeladen werden. Die vorhandenen Projekte bleiben nutzbar.', 5000);
    clearLoadMoreError();
  }, [clearLoadMoreError, loadMoreError, showToast]);

  useEffect(() => {
    if (!searchError) return;
    showToast('warning', searchError, 5000);
  }, [searchError, showToast]);

  useEffect(() => {
    const handleRefresh = () => {
      void fetchDeals({
        forceRefresh: true,
        cacheOwnerKey: user?.email || user?.id || null,
        stageLabel: dealStageFilter === 'all' ? undefined : DEAL_STAGE_FILTER_LABELS[dealStageFilter],
      });
    };

    window.addEventListener('project-status-refresh', handleRefresh);
    return () => window.removeEventListener('project-status-refresh', handleRefresh);
  }, [dealStageFilter, fetchDeals, user?.email, user?.id]);

  useEffect(() => {
    if (searchQuery.trim().length > 0) return;
    if (skipNextFilterFetchRef.current) {
      skipNextFilterFetchRef.current = false;
      return;
    }
    const hasClientOnlyFilters =
      nacharbeitenFilter !== 'all' ||
      interneFreigabeFilter !== 'all';
    if (dealStageFilter === 'all' && hasClientOnlyFilters) {
      if (deals.length === 0) {
        void fetchDeals({
          cacheOwnerKey: user?.email || user?.id || null,
          restoreUnfilteredFromCache: true,
        });
      }
      return;
    }
    const restoreUnfilteredFromCache =
      dealStageFilter === 'all' &&
      nacharbeitenFilter === 'all' &&
      interneFreigabeFilter === 'all';
    void fetchDeals({
      cacheOwnerKey: user?.email || user?.id || null,
      stageLabel: dealStageFilter === 'all' ? undefined : DEAL_STAGE_FILTER_LABELS[dealStageFilter],
      restoreUnfilteredFromCache,
    });
  }, [dealStageFilter, deals.length, fetchDeals, interneFreigabeFilter, nacharbeitenFilter, searchQuery, user?.email, user?.id]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) return;

    const timeoutId = window.setTimeout(() => {
      void searchDealsIntoCache(q, {
        cacheOwnerKey: user?.email || user?.id || null,
      });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchDealsIntoCache, searchQuery, user?.email, user?.id]);

  const enrichedDeals = useMemo<EnrichedDeal[]>(() => {
    const sourceDeals = searchQuery.trim().length > 0 ? (searchResults || []) : deals;
    return sourceDeals.map((deal) => {
      const documents = mapDealToDocuments(deal);
      const summary = getDealSummary(documents);
      const completionRatio = summary.total > 0 ? Math.round((summary.present / summary.total) * 100) : 0;
      const nextStep = documents.find((document) => document.required && document.status === 'missing')?.label
        || 'Nur noch optionale Unterlagen oder Kontrolle nötig';
      const projectStatus = deal.project_status;

      return {
        deal,
        completionRatio,
        summary,
        nextStep,
        phaseHealth: projectStatus?.phases || [],
        projectCompletionRatio: projectStatus?.overallCompletionPercent ?? completionRatio,
        projectProgressLoaded: !!projectStatus,
        projectRequiredMissing: projectStatus?.missingRequiredCount ?? summary.requiredMissing,
        currentPhaseKey: getCurrentPhaseKey(projectStatus?.phases || []),
      };
    });
  }, [deals, searchQuery, searchResults]);

  const filteredDeals = useMemo(() => {
    const next = enrichedDeals.filter((entry) => {
      if (searchQuery.trim().length > 0) {
        return true;
      }

      if (!matchesProjectScope(entry.deal, scope, user, { hubspotOwnerIds })) {
        return false;
      }
      if (nacharbeitenFilter === 'all') return true;
      const hasOpenNacharbeiten = Boolean(entry.deal.project_status?.nacharbeiten?.hasOpenNacharbeiten);
      return nacharbeitenFilter === 'yes' ? hasOpenNacharbeiten : !hasOpenNacharbeiten;
    }).filter((entry) => {
      if (interneFreigabeFilter === 'all') return true;
      const status = getInterneFreigabeStatus(entry.deal);
      if (interneFreigabeFilter === 'empty_or_draft') {
        return status === null || status === 'draft';
      }
      return status === interneFreigabeFilter;
    });

    next.sort((a, b) => {
      const left = getSortValue(a, sortKey);
      const right = getSortValue(b, sortKey);

      if (typeof left === 'number' && typeof right === 'number') {
        return sortDirection === 'asc' ? left - right : right - left;
      }

      const comparison = String(left).localeCompare(String(right), 'de');
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return next;
  }, [enrichedDeals, hubspotOwnerIds, interneFreigabeFilter, nacharbeitenFilter, scope, searchQuery, sortDirection, sortKey, user]);

  const handleSearchSubmit = async () => {
    const q = searchQuery.trim();
    if (q.length >= 3) {
      await searchDealsIntoCache(q, {
        cacheOwnerKey: user?.email || user?.id || null,
      });
    }
  };

  const setSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection(nextKey === 'dealname' || nextKey === 'dealstage' ? 'asc' : 'desc');
  };

  const renderSortHeader = (label: string, key: SortKey) => {
    const active = sortKey === key;

    return (
      <button
        type="button"
        onClick={() => setSort(key)}
        className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] ${active ? 'text-slate-950' : 'text-slate-500'}`}
      >
        {label}
        <span className="text-[10px]">{active ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    );
  };

  const resetFilters = () => {
    skipNextFilterFetchRef.current = true;
    setDealStageFilter('all');
    setNacharbeitenFilter('all');
    setInterneFreigabeFilter('all');
    void fetchDeals({
      cacheOwnerKey: user?.email || user?.id || null,
      restoreUnfilteredFromCache: true,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleScopeChange = (value: PersistedProjectScope) => {
    const hadSearch = searchQuery.trim().length > 0;
    setScope(value);
    persistProjectScope(value);
    setSearchQuery('');
    if (hadSearch) {
      resetFilters();
    }
  };

  const loadNacharbeitenForDeal = useCallback(async (dealId: string) => {
    if (nacharbeitenByDealId[dealId] || nacharbeitenLoadingByDealId[dealId]) return;
    setNacharbeitenLoadingByDealId((prev) => ({ ...prev, [dealId]: true }));
    setNacharbeitenErrorByDealId((prev) => ({ ...prev, [dealId]: '' }));
    try {
      const tickets = await getNacharbeitenTickets(dealId);
      const sorted = tickets.slice().sort((left, right) => {
        const leftTs = Date.parse(left.lastModifiedAt || left.createdAt || '') || 0;
        const rightTs = Date.parse(right.lastModifiedAt || right.createdAt || '') || 0;
        return rightTs - leftTs;
      });
      setNacharbeitenByDealId((prev) => ({ ...prev, [dealId]: sorted }));
    } catch {
      setNacharbeitenErrorByDealId((prev) => ({ ...prev, [dealId]: 'Nacharbeiten konnten nicht geladen werden.' }));
    } finally {
      setNacharbeitenLoadingByDealId((prev) => ({ ...prev, [dealId]: false }));
    }
  }, [nacharbeitenByDealId, nacharbeitenLoadingByDealId]);

  return (
    <div className="min-h-screen bg-transparent pb-20">
      <AppHeader
        title="360Volt Projekte"
        actions={(
          <ViewToggle />
        )}
        menuContent={(
          <button
            onClick={logout}
            className="mt-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
          >
            <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Abmelden
          </button>
        )}
      />
      <ProjectScopeTabs
        value={searchQuery.trim().length > 0 ? 'search' : scope}
        onChange={(value) => {
          if (value === 'search') return;
          handleScopeChange(value);
        }}
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onClearSearch={() => {
          setSearchQuery('');
        }}
        searchLoading={isSearching}
      />

      <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <DealStatusFilterBar
          dealStageFilter={dealStageFilter}
          nacharbeitenFilter={nacharbeitenFilter}
          interneFreigabeFilter={interneFreigabeFilter}
          onDealStageFilterChange={setDealStageFilter}
          onNacharbeitenFilterChange={setNacharbeitenFilter}
          onInterneFreigabeFilterChange={setInterneFreigabeFilter}
          onReset={resetFilters}
          onRefresh={() => {
            window.localStorage.removeItem('360volt-deals-store');
            window.location.reload();
          }}
          disabled={searchQuery.trim().length > 0}
        />

        {loading && deals.length === 0 && (
          <div className="flex items-center justify-center rounded-[28px] border border-slate-200/70 bg-white/90 py-16 shadow-[0_20px_70px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_70px_rgba(2,6,23,0.45)]">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-cyan-600 dark:border-slate-700 dark:border-t-cyan-400" />
            <span className="ml-4 text-sm font-medium text-slate-600 dark:text-slate-300">Deals werden geladen...</span>
          </div>
        )}

        {error && (
          <div className="rounded-[24px] border border-red-200 bg-red-50 px-5 py-4 text-red-800 shadow-sm dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">
            <p className="text-sm font-semibold">Fehler beim Laden</p>
            <p className="mt-1 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && filteredDeals.length === 0 && (
          <div className="rounded-[28px] border border-slate-200/70 bg-white/95 px-6 py-16 text-center shadow-[0_20px_70px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_70px_rgba(2,6,23,0.45)]">
            <h3 className="text-xl font-semibold text-slate-950 dark:text-slate-100">Keine Deals gefunden</h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {deals.length === 0
                ? 'In der konfigurierten Dealstage wurden aktuell keine Projekte gefunden.'
                : 'Mit dem aktuellen Scope und den aktiven Filtern gibt es keine Treffer.'}
            </p>
          </div>
        )}

        {!loading && !error && filteredDeals.length > 0 && (
          <section className="overflow-hidden rounded-[30px] border border-slate-200/70 bg-white/95 shadow-[0_20px_70px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_70px_rgba(2,6,23,0.45)]">
            <div className="border-b border-slate-200/70 px-5 py-4 dark:border-white/10 sm:px-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{filteredDeals.length} Projekte</p>
                  {(isLoadingMore || hasMore) && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Lade weitere Projekte nach...</p>
                  )}
                  {searchQuery.trim().length >= 3 && isSearching && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Weitere Suchergebnisse werden vom Server geladen...</p>
                  )}
                </div>
                <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 dark:bg-white/5 dark:text-slate-200">
                  Sortiert nach {sortKey === 'requiredMissing' ? 'Pflichtlücken' : sortKey === 'completion' ? 'Projektfortschritt' : sortKey === 'dealstage' ? 'Deal-Status' : 'Projektname'}
                </div>
              </div>
            </div>

            <div className="hidden xl:block overflow-x-auto">
              <table className="min-w-[1200px] divide-y divide-slate-200 dark:divide-white/10">
                <thead className="bg-slate-50/80 dark:bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left">{renderSortHeader('Projekt', 'dealname')}</th>
                    <th className="px-6 py-4 text-left">{renderSortHeader('Deal Status', 'dealstage')}</th>
                    <th className="px-6 py-4 text-left">{renderSortHeader('Projektfortschritt', 'completion')}</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Nacharbeiten</th>
                    <th className="w-[220px] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Nächster Schritt</th>
                    <th className="w-[180px] px-4 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/80 bg-white dark:divide-white/10 dark:bg-slate-900/40">
                  {filteredDeals.map((entry) => {
                    const isExpanded = expandedDealId === entry.deal.id;
                    return (
                      <Fragment key={entry.deal.id}>
                        <tr
                          key={entry.deal.id}
                          onClick={() => navigate(`/projects/${entry.deal.id}`)}
                          className="cursor-pointer transition hover:bg-slate-50/70 dark:hover:bg-white/5"
                        >
                          <td className="px-6 py-5 align-top">
                            <div className="min-w-0">
                              <div className="flex items-start gap-3">
                                <p className="font-semibold text-slate-950 dark:text-slate-100">{entry.deal.dealname || 'Unbenannter Deal'}</p>
                              </div>
                              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Deal ID {entry.deal.id}</p>
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700 dark:bg-white/5 dark:text-slate-200">
                              {entry.deal.dealstage_label || entry.deal.dealstage || 'Unbekannt'}
                            </span>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="w-[250px]">
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium text-slate-900 dark:text-slate-100">
                                  {entry.projectProgressLoaded ? `${entry.projectCompletionRatio}%` : 'Lädt...'}
                                </span>
                                <span className="text-slate-500 dark:text-slate-400">
                                  {entry.deal.project_status?.overallStatus === 'completed'
                                    ? 'Fertig'
                                    : entry.deal.project_status?.overallStatus === 'in_progress'
                                      ? 'In Arbeit'
                                      : 'Offen'}
                                </span>
                              </div>
                              <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                                <div className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-orange-500" style={{ width: `${entry.projectProgressLoaded ? entry.projectCompletionRatio : 8}%` }} />
                              </div>
                              <ProjectMilestoneTimeline
                                phases={entry.phaseHealth}
                                projektdokumentationApprovalStatus={entry.deal.project_status?.projektdokumentationApprovalStatus || null}
                                compact
                                className="mt-2 border-transparent bg-transparent px-0 py-0 shadow-none"
                              />
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${entry.deal.project_status?.nacharbeiten?.hasOpenNacharbeiten ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-700'}`}>
                              {entry.deal.project_status?.nacharbeiten?.hasOpenNacharbeiten
                                ? `${entry.deal.project_status.nacharbeiten.openNacharbeitenCount} offen`
                                : 'Nein'}
                            </span>
                          </td>
                          <td className="px-4 py-5 align-top">
                            <p className="max-w-[220px] text-sm text-slate-700 dark:text-slate-300">{entry.nextStep}</p>
                          </td>
                          <td className="px-4 py-5 align-top">
                            <div className="flex justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (isExpanded) {
                                    setExpandedDealId(null);
                                    return;
                                  }
                                  setExpandedDealId(entry.deal.id);
                                  void loadNacharbeitenForDeal(entry.deal.id);
                                }}
                                className="inline-flex h-10 min-w-[84px] items-center justify-center rounded-xl border border-slate-300 px-2.5 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
                              >
                                {isExpanded ? 'Weniger' : 'Details'}
                              </button>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(`/projects/${entry.deal.id}`);
                                }}
                                className="inline-flex h-10 min-w-[108px] items-center justify-center rounded-xl bg-slate-950 px-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                              >
                                Projekt öffnen
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${entry.deal.id}-details`} className="bg-slate-50/70 dark:bg-white/5">
                            <td colSpan={6} className="px-6 py-5">
                              <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Phasenstatus</p>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {entry.phaseHealth.map((phase) => (
                                      <span
                                        key={phase.phaseKey}
                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${phase.missingRequiredCount > 0 ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200' : 'border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-200'}`}
                                      >
                                        <span>{formatPhaseLabel(phase.phaseKey)}</span>
                                        <span className="rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold dark:bg-white/10">{phase.completionPercent}%</span>
                                        {phase.status === 'completed' && phase.completedByEmail && (
                                          <span className="text-[11px] text-slate-500 dark:text-slate-400">{phase.completedByEmail}</span>
                                        )}
                                        {phase.status === 'completed' && phase.approvedByEmail && (
                                          <span className="text-[11px] text-slate-500 dark:text-slate-400">Freigabe: {phase.approvedByEmail}</span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Dokumentenlage</p>
                                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950/80">
                                      <p className="text-xs text-slate-500 dark:text-slate-400">Gesamt</p>
                                      <p className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">{entry.summary.total}</p>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950/80">
                                      <p className="text-xs text-slate-500 dark:text-slate-400">Vorhanden</p>
                                      <p className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">{entry.summary.present}</p>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950/80">
                                      <p className="text-xs text-slate-500 dark:text-slate-400">Fehlen</p>
                                      <p className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">{entry.summary.missing}</p>
                                    </div>
                                  </div>
                                  {entry.deal.project_status?.nacharbeiten?.hasOpenNacharbeiten && (
                                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                                      <span className="font-semibold">{entry.deal.project_status.nacharbeiten.openNacharbeitenCount} offene Nacharbeiten</span>
                                      {entry.deal.project_status.nacharbeiten.latestNacharbeitenAt && (
                                        <span className="ml-2 text-amber-700 dark:text-amber-300">
                                          zuletzt {new Date(entry.deal.project_status.nacharbeiten.latestNacharbeitenAt).toLocaleDateString('de-DE')}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-950/80">
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Nacharbeiten Tickets</p>
                                    {nacharbeitenLoadingByDealId[entry.deal.id] ? (
                                      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Tickets werden geladen...</p>
                                    ) : nacharbeitenErrorByDealId[entry.deal.id] ? (
                                      <p className="mt-2 text-sm text-red-700 dark:text-red-300">{nacharbeitenErrorByDealId[entry.deal.id]}</p>
                                    ) : (nacharbeitenByDealId[entry.deal.id] || []).length === 0 ? (
                                      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Keine Nacharbeiten-Tickets vorhanden.</p>
                                    ) : (
                                      <ul className="mt-2 space-y-2">
                                        {(nacharbeitenByDealId[entry.deal.id] || []).map((ticket) => (
                                          <li key={ticket.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2 dark:border-white/10">
                                            <span className="text-sm text-slate-800 dark:text-slate-200">
                                              #{ticket.id} {ticket.open ? '(Offen)' : '(Geschlossen)'}
                                            </span>
                                            <a
                                              href={`https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-5/${encodeURIComponent(ticket.id)}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-950 dark:text-slate-200"
                                            >
                                              In HubSpot öffnen
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 p-4 xl:hidden">
              {filteredDeals.map((entry) => (
                <button
                  key={entry.deal.id}
                  type="button"
                  onClick={() => navigate(`/projects/${entry.deal.id}`)}
                  className="w-full rounded-[24px] border border-slate-200 bg-slate-50/70 p-4 text-left dark:border-white/10 dark:bg-white/5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-950 dark:text-slate-100">{entry.deal.dealname || 'Unbenannter Deal'}</p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{entry.deal.dealstage_label || entry.deal.dealstage || 'Unbekannt'}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${entry.projectRequiredMissing > 0 ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-800'}`}>
                      {entry.projectRequiredMissing} offen
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{entry.nextStep}</p>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Projektfortschritt</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{entry.projectProgressLoaded ? `${entry.projectCompletionRatio}%` : 'Lädt...'}</span>
                  </div>
                  {entry.deal.project_status?.nacharbeiten?.hasOpenNacharbeiten && (
                    <div className="mt-3 inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                      {entry.deal.project_status.nacharbeiten.openNacharbeitenCount} offene Nacharbeiten
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
